(function () {
  "use strict";
  /* BVO — studio backdrop normaliser. Replaces bvo-fit.js (8 Sep 2026).

     WHAT IT REPLACED. bvo-fit.js measured every photo in the browser and rescaled it
     to even out Luxottica's inconsistent framing (66%-94% of the frame). That job now
     lives in the image files: all 1,441 featured photos were rebuilt on 7 Sep as 4:3
     with the frame at a uniform 90%. Left running, the old script sized the main photo
     against the median of the UN-rebuilt thumbnails, so it shrank a correct photo to
     match wrong ones — 86.6% on rb4428-classic, smaller again on every click, never
     restoring. That was the "it jumps then stays small" complaint.
     THIS SCRIPT DOES NO RESIZING AT ALL.

     WHAT IT DOES. Tiles are painted in --shot, the studio grey Luxottica bakes into its
     photography, so a framed shot blends into the tile instead of reading as a box. The
     rebuilt featured photos are on pure white — deliberately, that is the file Google
     Shopping reads. This normalises what is DISPLAYED to --shot: a photo whose corner is
     a neutral opaque tone gets a linear brightness nudge so its backdrop lands exactly
     on the tile colour. White files show at 242 on the shelf; untouched 242 supplier
     photos are left alone; transparent grounds already show the tile through and are
     skipped; coloured or dark corners are not backdrops and are never touched.

     --shot is the single source of truth. Change it in bvo.css and the photography
     follows — no numbers to keep in sync.

     TWO RACES THIS HAS ALREADY BEEN BITTEN BY, both in the product gallery, which
     reuses ONE <img> and swaps its src:

     1. CACHING. An earlier version cached the measurement per src. The observer could
        sample the previous (still-painted) photo and file the answer under the NEW src,
        where it stuck — photo 2 inherited photo 1's answer forever. There is no cache.
        A 4x4 read is microseconds; measuring every time is simply correct.

     2. DECODE. img.complete goes true and the load event fires before the new frame is
        necessarily decoded, so drawImage could still return the OLD pixels — photo 2
        measured as 255 and kept photo 1's correction. Every measurement now waits on
        img.decode(), which resolves only when the frame is ready to paint.

     It sets data-bvo-fitted so the reveal gate in theme.liquid still lifts. */

  var SEL = '.pcard__media img, [data-thumb] img, [data-main-image], .swatch img, .cart-item__media img';
  var MIN_BG = 200;   /* darker than this is not a studio backdrop */
  var NEUTRAL = 6;    /* max channel spread still counted as neutral */
  var FALLBACK = 242; /* Luxottica's backdrop, if --shot cannot be read */

  function targetLevel() {
    var v = '', m;
    try { v = getComputedStyle(document.documentElement).getPropertyValue('--shot').trim(); } catch (e) {}
    if ((m = /^#([0-9a-f]{6})$/i.exec(v))) {
      var n = parseInt(m[1], 16);
      return Math.max((n >> 16) & 255, (n >> 8) & 255, n & 255);
    }
    if ((m = /^#([0-9a-f]{3})$/i.exec(v))) {
      var h = m[1];
      return Math.max(parseInt(h[0] + h[0], 16), parseInt(h[1] + h[1], 16), parseInt(h[2] + h[2], 16));
    }
    if ((m = /rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(v))) {
      return Math.max(+m[1], +m[2], +m[3]);
    }
    return FALLBACK;
  }

  function mark(img) {
    if (img && !img.hasAttribute('data-bvo-fitted')) img.setAttribute('data-bvo-fitted', '1');
  }

  /* average the top-left 4x4 source pixels of whatever is decoded right now */
  function backdrop(img) {
    if (!img.naturalWidth || !img.naturalHeight) return 0;
    var c = document.createElement('canvas');
    c.width = 4; c.height = 4;
    var ctx = c.getContext('2d', { willReadFrequently: true }), d;
    try {
      ctx.drawImage(img, 0, 0, 4, 4, 0, 0, 4, 4);
      d = ctx.getImageData(0, 0, 4, 4).data;
    } catch (e) {
      return 0;                                   /* tainted canvas — leave alone */
    }
    var r = 0, g = 0, b = 0, a = 0, n = 16, i;
    for (i = 0; i < n; i++) { r += d[i * 4]; g += d[i * 4 + 1]; b += d[i * 4 + 2]; a += d[i * 4 + 3]; }
    r /= n; g /= n; b /= n; a /= n;
    if (a < 250) return 0;                        /* transparent — tile shows through */
    var mn = Math.min(r, g, b), mx = Math.max(r, g, b);
    if (mx - mn > NEUTRAL) return 0;              /* coloured — not a backdrop */
    if (mx < MIN_BG) return 0;                    /* dark — not a backdrop */
    return mx;
  }

  function settle(img, target) {
    var level = backdrop(img);
    if (level) {
      var k = target / level;
      var want = Math.abs(k - 1) > 0.005 ? 'brightness(' + k.toFixed(4) + ')' : '';
      if (img.style.filter !== want) img.style.filter = want;
    }
    mark(img);
  }

  function apply(img, target) {
    if (!img || !img.complete || !img.naturalWidth) return;
    var done = false;
    var run1 = function () {
      if (done) return;
      done = true;
      try { settle(img, target); } catch (e) { mark(img); }
    };
    /* RACE 4 (8 Sep 2026): decode() is the right signal for RACE 2, but on this
       Chrome the promise for an already-complete, already-painted image never
       settles - neither resolve nor reject. Instrumented on rb4428-classic: apply
       passed every guard once, settle ran ZERO times, so no photo was ever marked
       and the whole correction silently did nothing on most page loads. decode()
       now races a short timer and whichever fires first wins. settle re-reads the
       pixels at call time, so a late run is still measuring the right frame. */
    if (img.decode) { img.decode().then(run1, run1); setTimeout(run1, 250); }
    else { run1(); }
  }

  function run() {
    var target = targetLevel();
    var list = document.querySelectorAll(SEL);
    for (var i = 0; i < list.length; i++) {
      var img = list[i];
      if (img.complete && img.naturalWidth) { apply(img, target); continue; }
      /* RACE 5 (8 Sep 2026): the document-level load listener never fires for these
         images (measured: zero IMG load events on a full page load), and lazily loaded
         card images can finish well after the timed sweeps below. A rebuilt photo is a
         WHITE file, so one that misses its correction paints as a bright white box
         inside the grey tile - which is what Morris saw on hover for a few products,
         and would have got worse as more gallery photos were rebuilt to white.
         Hooking each image directly is the one signal that cannot be missed. */
      if (!img.__bvoHooked) {
        img.__bvoHooked = true;
        img.addEventListener('load', function () { apply(this, targetLevel()); }, { once: true });
        img.addEventListener('error', function () { mark(this); }, { once: true });
      }
    }
  }

  var pending = null;
  function schedule() {
    if (pending) cancelAnimationFrame(pending);
    pending = requestAnimationFrame(function () { pending = null; try { run(); } catch (e) {} });
  }

  function boot() {
    /* RACE 3 (8 Sep 2026): listeners MUST be attached before the first run().
       Previously run() came first. With images served from cache they finished
       loading in the microseconds between run() and addEventListener, so their
       load events were missed and nothing ever re-measured — the correction
       applied on some page loads and not others. Attach first, measure second,
       and re-measure on window load. */
    document.addEventListener('load', function (e) {
      var t = e.target;
      if (t && t.tagName === 'IMG' && t.closest &&
          t.closest('.pcard__media, [data-thumb], .product__main-media, .swatch, .cart-item__media')) schedule();
    }, true);
    /* a photo that fails to load must still be revealed, never left at opacity 0 */
    document.addEventListener('error', function (e) {
      var t = e.target; if (t && t.tagName === 'IMG') mark(t);
    }, true);
    var main = document.querySelector('[data-main-image]');
    if (main) new MutationObserver(schedule).observe(main, { attributes: true, attributeFilter: ['src', 'srcset'] });
    var thumbRow = document.querySelector('[data-thumbs]');
    if (thumbRow) new MutationObserver(schedule).observe(thumbRow, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, { passive: true });   /* lazy cards arrive on scroll */
    window.addEventListener('load', schedule);   /* backstop: every image is in by now */
    try { run(); } catch (e) {}
    /* Lazy thumbnails can finish without their load event ever reaching the
       listener above - measured zero IMG load events at document level on a full
       rb4428 page load - so sweep a few times as well. Six 4x4 canvas reads is
       nothing, and settle() is idempotent. */
    setTimeout(schedule, 300);
    setTimeout(schedule, 900);
    setTimeout(schedule, 2000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}());
